import apiClient from "./middleware";

export interface ParsedVideoItem {
  videoId: string;
  duration: string;
  music_note: boolean;
}

export interface MusicClassifierResultRow {
  videoId: string;
  title?: string;
  description?: string;
  duration?: string;
  /** View count from YouTube API (when available). */
  views?: number;
  /** From HTML parse: music icon/signal; "excluded" for API-only videos not in HTML. */
  music_note?: boolean | "excluded";
  is_musical_opportunity: boolean;
  category: string;
  confidence: number;
  error?: string;
}

export interface ExtraVideoItem {
  videoId: string;
  title: string;
  description: string;
  duration: string;
}

/** Run document (from getRun or classify response). */
export interface MusicClassifierRunDocument {
  runId: string;
  channelId?: string;
  dateCreated: string;
  results: MusicClassifierResultRow[];
  createdBy?: string;
  truncatedResults?: boolean;
  totalResultCount?: number;
  channelInMusicCms?: boolean;
  /** Channel metadata from YouTube API (when channelId was set). */
  channelTitle?: string;
  channelSubscriberCount?: number;
  channelDefaultLanguage?: string;
  channelMadeForKids?: boolean;
  channelContentOwner?: string;
}

export const musicClassifierApi = {
  parseHtml: async (
    html: string
  ): Promise<{ videos: ParsedVideoItem[]; channelId?: string }> => {
    const { data } = await apiClient.post<{
      videos: ParsedVideoItem[];
      channelId?: string;
    }>("/music-classifier/parse-html", { html });
    return data;
  },

  fetchChannelVideos: async (params: {
    channelId: string;
    videoIdsFromPage: string[];
  }): Promise<{
    fromHtml: number;
    extraFromChannel: number;
    extraVideos: ExtraVideoItem[];
  }> => {
    const { data } = await apiClient.post("/music-classifier/fetch-channel-videos", params);
    return data;
  },

  classify: async (params: {
    videos: { videoId: string; title?: string; description?: string; duration?: string; music_note?: boolean; fromChannelApi?: boolean }[];
    channelId?: string;
  }): Promise<{
    runId: string;
    results: MusicClassifierResultRow[];
    errors: { videoId: string; error: string }[];
    channelSummary?: { fromHtml: number; extraFromChannel: number };
    truncatedResults?: boolean;
    totalResultCount?: number;
    channelInMusicCms?: boolean;
    channelTitle?: string;
    channelSubscriberCount?: number;
    channelDefaultLanguage?: string;
    channelMadeForKids?: boolean;
    channelContentOwner?: string;
  }> => {
    const { data } = await apiClient.post("/music-classifier/classify", params);
    return data;
  },

  listRuns: async (params?: {
    channelId?: string;
    limit?: number;
  }): Promise<{ runs: MusicClassifierRunDocument[] }> => {
    const { data } = await apiClient.get<{ runs: MusicClassifierRunDocument[] }>(
      "/music-classifier/runs",
      { params }
    );
    return data;
  },

  getRun: async (runId: string): Promise<MusicClassifierRunDocument> => {
    const { data } = await apiClient.get(`/music-classifier/runs/${runId}`);
    return data as MusicClassifierRunDocument;
  },

  getExportUrl: (runId: string): string => {
    const base = process.env.REACT_APP_SERVER_URL || "http://localhost:8080";
    return `${base}/music-classifier/runs/${runId}/export`;
  },

  downloadExport: async (
    runId: string,
    options?: { filename?: string; compact?: boolean }
  ): Promise<void> => {
    const params = options?.compact ? { compact: "1" } : undefined;
    const response = await apiClient.get(`/music-classifier/runs/${runId}/export`, {
      responseType: "blob",
      params,
    });
    const contentDisposition = response.headers?.["content-disposition"];
    const serverFilename = contentDisposition
      ?.split("filename=")[1]
      ?.replace(/^["']|["']$/g, "")
      ?.trim();
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const name = options?.filename ?? serverFilename ?? `music-classifier-${runId}.csv`;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
